/**
 * Heuristic detection of personally-identifiable field names.
 *
 * Mirrors the approach used by `src/lib/sentryScrub.ts` (key-name heuristics),
 * but targets *identity* fields (name/email/contact) rather than secrets
 * (tokens/passwords). Use together, not as a replacement for one another.
 *
 * As with the Sentry scrubber: this is a heuristic. Novel field names may
 * need to be added here. Prefer explicit allow-lists (`getAllowedIdentifierFields`
 * in `modes.ts`) over relying solely on this deny-list when building new
 * exports.
 */

/**
 * Normalize a key for matching: lowercase, strip separators. This makes
 * `playerName`, `player_name`, `PLAYER-NAME` and `player name` all match the
 * same pattern, since real codebases mix camelCase (JS/TS) and snake_case
 * (Postgres/Supabase) freely.
 */
function normalizeKey(key: string): string {
  return key.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Normalized (separator-free, lowercase) key names/substrings treated as PII. */
const PII_KEY_PATTERNS = [
  'email',
  'fullname',
  'firstname',
  'lastname',
  'displayname',
  'playername',
  'studentname',
  'teachername',
  'parentname',
  'guardianname',
  'nickname',
  'phone',
  'address',
  'postalcode',
  'zipcode',
  'ipaddress',
  'useragent',
  'dateofbirth',
  'dob',
  'ssn',
  'personalid',
  'henkilotunnus',
  'socialsecuritynumber',
].map(normalizeKey);

/** Additional identity / device keys treated as PII for anonymous analytics. */
const EXTRA_PII_PATTERNS = ['studentid', 'userid', 'authid', 'deviceid', 'fingerprint', 'avatar'].map(
  normalizeKey,
);

const ALL_PII_PATTERNS = [...PII_KEY_PATTERNS, ...EXTRA_PII_PATTERNS];

/**
 * Exact-match-only patterns: short/ambiguous tokens that would cause false
 * positives as substrings (e.g. "ip" inside "shipDate", "dob" inside
 * "adobe"), so they are only flagged when they are the *entire* normalized
 * key.
 */
const PII_KEY_EXACT_ONLY = new Set(['ip', 'dob', 'ssn'].map(normalizeKey));

export function isPiiField(key: string): boolean {
  const normalized = normalizeKey(key);
  if (!normalized) return false;
  if (PII_KEY_EXACT_ONLY.has(normalized)) return true;
  return ALL_PII_PATTERNS.some((pattern) => {
    if (PII_KEY_EXACT_ONLY.has(pattern)) return false;
    return normalized.includes(pattern);
  });
}

/**
 * `display_name` / `player_name` are intentionally treated as PII by
 * `isPiiField` because they carry a real name in some contexts. In
 * `school_managed` mode, the teacher's own scoped student roster is allowed
 * to show these — see `getAllowedIdentifierFields('school_managed')`. This
 * helper is for call sites that already know the field is an
 * explicitly-allowed identity field for the current mode.
 */
export function isExplicitlyAllowedField(key: string, allowedFields: readonly string[]): boolean {
  return allowedFields.includes(key);
}

export { PII_KEY_PATTERNS, normalizeKey };
